/* =========================================================
   The Circle · "Your take" — the friend's side of a shared link.
   Screens: gate → vote deck → compare → done  (+ closed/deleted)

   Backed by the VRO app: the picks are the real items on the shared
   list, and every take is written against the real share token.

   Login: the gate hands off to Shopify's own hosted login in a popup
   (window.VroCircleApi.login) — Shopify is the only thing that can
   create a real session, and it won't do it inside our own UI. The
   phone/OTP sheets in snippets/circle-take.liquid are left in place but
   unused; if this store ever wires its Firebase login to the app's
   backend (see the app's own notes on accepting that session), the gate
   can be pointed at them instead without touching anything else here.

   Takes are saved as they happen, not only at the end — closing the tab
   halfway through leaves the takes already given on the list, because
   the backend upserts each one.
   ========================================================= */
(function () {
  'use strict';

  var Api = window.VroCircleApi;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var overlay = document.getElementById('ctOverlay');
    if (!overlay || !Api || overlay.__ctWired) return;
    overlay.__ctWired = true;
    wire(overlay);
  });

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(paise) {
    if (paise == null) return '';
    return '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  /* Shopify CDN URLs usually carry ?v=..., but not always — appending
     "&width=" blindly builds a broken URL when they don't. */
  function withWidth(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function wire(overlay) {
    /* ---- state ---- */
    var token = null;
    var preview = false;
    var picks = [];                     // [{ id: list_item_id, brand, name, price, image, thumb }]
    var votes = {};                     // list_item_id -> 'yes' | 'nah'
    var notes = {};                     // list_item_id -> string
    var owner = overlay.getAttribute('data-owner') || 'They';
    var listUrl = overlay.getAttribute('data-list-url') || '';
    var idx = 0;
    var isOpen = false;
    var lastFocus = null;

    var screens = {};
    overlay.querySelectorAll('[data-ct-screen]').forEach(function (s) {
      screens[s.getAttribute('data-ct-screen')] = s;
    });
    var sheets = {};
    overlay.querySelectorAll('[data-ct-sheet]').forEach(function (s) {
      sheets[s.getAttribute('data-ct-sheet')] = s;
    });
    var sheetScrim = overlay.querySelector('[data-ct-sheet-scrim]');
    var hintEl = overlay.querySelector('[data-ct-hint]');

    /* ---- saving ----
       Everything currently voted goes up in one call, debounced, because
       the backend takes the whole set and upserts it. That makes a repeat
       save harmless and means an edit on the compare screen needs no
       special path of its own. */
    var saveTimer = null;
    var saveInFlight = null;

    function payload() {
      return Object.keys(votes).map(function (id) {
        return {
          item_id: Number(id),
          vote: votes[id] === 'yes' ? 'up' : 'down',
          note: notes[id] || null
        };
      });
    }

    function flushSaves() {
      clearTimeout(saveTimer);
      if (preview || !token) return Promise.resolve();
      var picksNow = payload();
      if (!picksNow.length) return Promise.resolve();

      saveInFlight = Api.call(function (extra) {
        return Api.api('/s/' + encodeURIComponent(token) + '/vote', {
          method: 'POST',
          body: Object.assign({ picks: picksNow }, extra)
        });
      }).catch(function (err) {
        if (err.code === 'consent_declined') return;
        if (err.status === 401) { screen('gate'); return; }
        if (err.status === 409) { showClosed(err.code); return; }
        // Say WHY. These are the cases a silent "couldn't save" would
        // have left someone tapping at a flow that can never work.
        var reason = {
          cannot_vote_own_list: 'This is your own list — open it from your Circle to see the takes.',
          view_only: 'This link is view-only.',
          no_valid_items: 'Those items are no longer on the list.'
        }[err.code];
        if (window.showToast) window.showToast(reason || "Couldn't save that take.");
      });
      return saveInFlight;
    }

    function queueSave() {
      if (preview) return;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(flushSaves, 600);
    }

    /* ---- open / close ---- */
    function openPopup() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      overlay.hidden = false;
      document.body.classList.add('ct-lock');
      void overlay.offsetWidth;
      overlay.classList.add('is-open');
    }

    function closePopup() {
      if (!isOpen) return;
      isOpen = false;
      flushSaves();
      closeSheet();
      overlay.classList.remove('is-open');
      document.body.classList.remove('ct-lock');
      setTimeout(function () { if (!isOpen) overlay.hidden = true; }, 260);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    overlay.querySelectorAll('[data-ct-close]').forEach(function (b) {
      b.addEventListener('click', closePopup);
    });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closePopup(); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !isOpen) return;
      if (anySheetOpen()) closeSheet(); else closePopup();
    });

    /* ---- screens ---- */
    function screen(name) {
      if (!screens[name]) return;
      var order = ['gate', 'vote', 'compare', 'done', 'deleted'];
      Object.keys(screens).forEach(function (k) {
        screens[k].classList.toggle('is-active', k === name);
        screens[k].classList.toggle('is-past', k !== name && order.indexOf(k) < order.indexOf(name));
      });
      if (name === 'gate') renderDeck('gate', 0);
      if (name === 'vote') renderDeck('vote', idx);
      if (name === 'compare') renderList();
      if (name === 'done') renderRecap();
      if (screens[name]) screens[name].scrollTop = 0;
    }

    function showClosed(code) {
      var titleEl = screens.deleted && screens.deleted.querySelector('.ct-end-title');
      var subEl = screens.deleted && screens.deleted.querySelector('.ct-end-sub');
      if (titleEl) {
        titleEl.textContent = code === 'share_expired' ? 'This link has expired'
          : code === 'list_deleted' ? owner + ' deleted this list'
          : owner + ' closed this list';
      }
      if (subEl) subEl.textContent = 'The link isn’t live any more';
      screen('deleted');
    }

    /* ---- loading the shared list ---- */
    function toPicks(items) {
      return (items || []).map(function (item) {
        var snap = item.snapshot || {};
        return {
          id: item.id,
          brand: snap.vendor || '',
          name: snap.title || 'Product',
          price: money(snap.price),
          image: withWidth(snap.image, 740),
          thumb: withWidth(snap.image, 240),
          handle: snap.handle || ''
        };
      });
    }

    /* A tap on the card (not a swipe) opens the real product page — in a
       new tab, so the vote in progress is still there when the voter
       comes back to it. */
    function openProductPage(pick) {
      if (!pick || !pick.handle) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.open(root + 'products/' + pick.handle, '_blank', 'noopener');
    }

    function load() {
      return Api.api('/s/' + encodeURIComponent(token))
        .then(function (data) {
          owner = data.owner_name || owner;

          /* The owner opening their own link — which is what happens
             every time they check it. Voting would 403 on the first
             swipe, so send them to their own list instead, where the
             takes they're actually looking for are. Preview mode is the
             exception: that IS the owner, deliberately looking at the
             friend's side, and it saves nothing anyway. */
          if (data.is_owner && !preview) {
            closePopup();
            if (window.VroCirclePanel && window.VroCirclePanel.open) window.VroCirclePanel.open();
            else if (window.showToast) window.showToast('This is your own list.');
            return;
          }

          picks = toPicks(data.items);

          votes = {};
          notes = {};
          (data.my_votes || []).forEach(function (v) {
            votes[v.list_item_id] = v.vote_value === 'up' ? 'yes' : 'nah';
            if (v.note) notes[v.list_item_id] = v.note;
          });

          applyCopy();

          if (!picks.length) { showClosed('empty'); return; }

          // Someone coming back to takes they already gave lands on the
          // list rather than swiping the same cards again.
          var answered = Object.keys(votes).length;
          idx = Math.min(answered, picks.length);

          // A first-time voter meets ONE combined prompt for whichever of
          // name/consent is still missing, before the deck — not a name
          // dialog now and a separate consent wall on the first swipe.
          return Api.api('/me').then(function (me) {
            if (me && (!me.display_name || !me.has_consent)) {
              return openIdentitySheet(me).then(enterDeck);
            }
            enterDeck();
          }).catch(enterDeck);   // guest-ish edge case: let the deck's own 401 handling take it from here

          function enterDeck() {
            if (answered >= picks.length) screen('compare');
            else screen('vote');
          }
        })
        .catch(function (err) {
          if (err.status === 401) {
            owner = (err.data && err.data.owner_name) || owner;
            // The picks come back even before signing in, so the gate
            // shows what's actually being asked about rather than an
            // empty deck.
            picks = toPicks(err.data && err.data.items);
            applyCopy();
            screen('gate');
            return;
          }
          if (err.status === 410) {
            // Who closed it, when there's someone to name — a deleted or
            // revoked share carries owner_name; "share_expired" doesn't
            // need one, nothing changes if it's missing either way.
            owner = (err.data && err.data.owner_name) || owner;
            showClosed(err.code);
            return;
          }
          if (err.status === 404) { showClosed('not_found'); return; }
          showClosed('error');
        });
    }

    function applyCopy() {
      var n = picks.length;
      var nText = n ? n + ' pick' + (n === 1 ? '' : 's') : 'their picks';
      overlay.querySelectorAll('.ct-title').forEach(function (el) {
        el.textContent = owner + ' wants your take';
      });
      overlay.querySelectorAll('[data-ct-picktext]').forEach(function (el) {
        el.textContent = el.getAttribute('data-ct-picktext') === 'gate'
          ? nText + ' · sign in to give your take'
          : nText + ' · about 15 seconds';
      });
      var seeAll = overlay.querySelector('[data-ct-seeall]');
      if (seeAll && n) seeAll.textContent = 'See all ' + n;
      var cmpTitle = overlay.querySelector('[data-ct-comparetitle]');
      if (cmpTitle) cmpTitle.textContent = owner + '’s ' + (n || '') + ' picks';
      var gateNote = overlay.querySelector('.ct-note');
      if (gateNote) gateNote.textContent = owner + ' sees your name with your take';
      overlay.querySelectorAll('.ct-end-sub').forEach(function (el) {
        if (el.closest('[data-ct-screen="done"]')) el.textContent = owner + ' will see your take';
      });
    }

    /* ---- deck ---- */
    function cardHTML(pick, i) {
      return '' +
        '<div class="ct-card ct-card--b3"></div>' +
        '<div class="ct-card ct-card--b2"></div>' +
        '<div class="ct-card ct-card--front" data-ct-front>' +
          (pick.image ? '<img class="ct-card-img" src="' + esc(pick.image) + '" alt="">' : '') +
          '<div class="ct-card-scrim"></div>' +
          '<div class="ct-card-count">' + (i + 1) + ' of ' + picks.length + '</div>' +
          '<div class="ct-card-cap">' +
            '<p class="ct-card-brand">' + esc(pick.brand) + '</p>' +
            '<p class="ct-card-name">' + esc(pick.name) + '</p>' +
            '<p class="ct-card-price">' + esc(pick.price) + '</p>' +
          '</div>' +
        '</div>';
    }

    /* Reaching the end of the deck used to jump straight to the compare
       screen on its own — the swipe screen was gone the instant the last
       card was, with nothing the voter did to end up there. Now the last
       card just leaves a "that's all of them" card behind with its own
       button, so leaving the deck is something the voter presses, not
       something that happens to them. */
    function renderDeck(which, i) {
      var deck = overlay.querySelector('[data-ct-deck="' + which + '"]');
      if (!deck || !picks.length) return;
      if (which === 'vote' && i >= picks.length) {
        overlay.classList.add('is-deck-done');
        deck.innerHTML =
          '<div class="ct-deck-done">' +
            '<span class="ct-deck-done-mark">' +
              '<svg width="18" height="13" viewBox="0 0 20 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 7l6 6L19 1"/></svg>' +
            '</span>' +
            '<p class="ct-deck-done-title">That&rsquo;s all of them</p>' +
            '<button type="button" class="ct-btn ct-btn--dark" data-ct-goto="compare">Review your picks</button>' +
          '</div>';
        setHint('', '');
        return;
      }
      overlay.classList.remove('is-deck-done');
      var at = Math.min(i, picks.length - 1);
      deck.innerHTML = cardHTML(picks[at], at);
      if (which === 'vote') {
        setHint('Swipe right to keep', '');
        bindDrag(deck.querySelector('[data-ct-front]'));
      }
    }

    function setHint(text, lean) {
      if (!hintEl) return;
      hintEl.textContent = text;
      hintEl.classList.toggle('lean-yes', lean === 'yes');
      hintEl.classList.toggle('lean-nah', lean === 'nah');
    }

    // Set the instant a card starts leaving, cleared once the next one is
    // up. castVote() re-entering during that ~300ms exit animation used to
    // be possible — a second swipe landing before advance() ran fired a
    // second castVote() on the SAME still-current card, silently flipping
    // an already-recorded take or double-opening the note sheet. The card
    // itself is also pointer-events:none for the same window (see
    // .gone-yes/.gone-nah in circle-take.css) — between the two, a card
    // only ever takes one vote, and only stays swipeable up to that point.
    var votingInProgress = false;

    function castVote(kind) {
      if (idx >= picks.length || votingInProgress) return;
      votingInProgress = true;
      var pick = picks[idx];
      votes[pick.id] = kind;
      queueSave();

      var front = overlay.querySelector('[data-ct-deck="vote"] [data-ct-front]');
      if (front) front.classList.add(kind === 'yes' ? 'gone-yes' : 'gone-nah');
      if (kind === 'yes') setHint('Yes, keeping it', 'yes');

      setTimeout(function () {
        votingInProgress = false;
        if (kind === 'yes') openNote(pick.id, advance);
        else advance();
      }, 300);
    }

    function advance() {
      idx += 1;
      flushSaves();
      // Saved in the background either way; renderDeck itself shows the
      // "that's all of them" card once idx runs past the end, rather than
      // this navigating anywhere.
      renderDeck('vote', idx);
    }

    function bindDrag(card) {
      if (!card) return;
      var x0 = 0, dx = 0, dragging = false;
      card.addEventListener('pointerdown', function (e) {
        dragging = true; x0 = e.clientX; dx = 0;
        card.style.transition = 'none';
        try { card.setPointerCapture(e.pointerId); } catch (err) {}
      });
      card.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        dx = e.clientX - x0;
        card.style.transform = 'translateX(calc(-50% + ' + dx + 'px)) rotate(' + (dx / 22) + 'deg)';
        if (dx > 45) setHint('Yes, keeping it', 'yes');
        else if (dx < -45) setHint('Nah', 'nah');
        else setHint('Swipe right to keep', '');
      });
      function end() {
        if (!dragging) return;
        dragging = false;
        card.style.transition = '';
        card.style.transform = '';
        if (dx > 95) castVote('yes');
        else if (dx < -95) castVote('nah');
        // Barely moved — a tap, not a swipe attempt. Same card, so still
        // the one at picks[idx].
        else if (Math.abs(dx) < 10) openProductPage(picks[idx]);
        else setHint('Swipe right to keep', '');
        dx = 0;
      }
      card.addEventListener('pointerup', end);
      card.addEventListener('pointercancel', end);
    }

    /* ---- identity sheet (name + consent, asked once) ----
       Shown right after load() confirms one or both are missing, before
       the deck. A combined prompt instead of two separate ones: a name
       dialog now, and — on the very first swipe, whenever that happened
       to land — a consent wall thrown up by the backend's 403. This asks
       both at once and gets out of the way for good once they're answered. */
    var idEls = {
      row: overlay.querySelector('[data-ct-identity-name-row]'),
      name: overlay.querySelector('[data-ct-identity-name]'),
      consentRow: overlay.querySelector('[data-ct-identity-consent-row]'),
      consent: overlay.querySelector('[data-ct-identity-consent]'),
      go: overlay.querySelector('[data-ct-identity-continue]'),
      copy: overlay.querySelector('[data-ct-identity-copy]')
    };
    var pendingIdentity = null;   // { resolve, needConsent }

    function updateIdentityBtn() {
      if (!idEls.go) return;
      idEls.go.disabled = Boolean(pendingIdentity && pendingIdentity.needConsent && idEls.consent && !idEls.consent.checked);
    }

    function openIdentitySheet(me) {
      if (!idEls.go || !sheets.identity) return Promise.resolve();
      var needName = !(me && me.display_name);
      var needConsent = !(me && me.has_consent);
      if (!needName && !needConsent) return Promise.resolve();

      if (idEls.row) idEls.row.hidden = !needName;
      if (idEls.consentRow) idEls.consentRow.hidden = !needConsent;
      if (idEls.copy) {
        idEls.copy.textContent = needConsent
          ? owner + ' — and whoever else has this link — will see your take.'
          : owner + ' will see your name with your take.';
      }
      if (idEls.name) idEls.name.value = '';
      if (idEls.consent) idEls.consent.checked = false;

      return new Promise(function (resolve) {
        pendingIdentity = { resolve: resolve, needConsent: needConsent };
        updateIdentityBtn();
        openSheet('identity');
        setTimeout(function () { if (needName && idEls.name) idEls.name.focus(); }, 260);
      });
    }

    if (idEls.consent) idEls.consent.addEventListener('change', updateIdentityBtn);

    if (idEls.go) {
      idEls.go.addEventListener('click', function () {
        var p = pendingIdentity;
        if (!p) return;
        if (p.needConsent && idEls.consent && !idEls.consent.checked) return;   // belt & suspenders — button is disabled already

        var jobs = [];
        var name = idEls.name ? idEls.name.value.trim() : '';
        if (name) jobs.push(Api.api('/me', { method: 'PUT', body: { display_name: name } }));
        if (p.needConsent) jobs.push(Api.api('/consent', { method: 'POST', body: { notice_version: Api.CONSENT_VERSION } }));

        Api.busy(idEls.go, true);
        Promise.all(jobs).catch(function () { /* voting still works; the sheet just asks again next time */ })
          .then(function () {
            pendingIdentity = null;
            // Cleared before the sheet goes, not after: this element is
            // reused if the sheet ever opens again, and a busy flag left
            // on it would come back as a spinner over a dead button.
            Api.busy(idEls.go, false);
            closeSheet();
            p.resolve();
          });
      });
    }

    /* ---- note ("say why") sheet ---- */
    var noteField = overlay.querySelector('[data-ct-note]');
    var noteCount = overlay.querySelector('[data-ct-note-count]');
    var notePickId = null;
    var noteDone = null;

    function openNote(pickId, done) {
      notePickId = pickId;
      noteDone = done;
      if (noteField) {
        noteField.value = notes[pickId] || '';
        updateNoteCount();
      }
      openSheet('note');
      setTimeout(function () { if (noteField) noteField.focus(); }, 260);
    }

    function finishNote(store) {
      if (store && noteField) {
        var v = noteField.value.trim();
        if (v) notes[notePickId] = v; else delete notes[notePickId];
        queueSave();
      }
      closeSheet();
      var d = noteDone;
      noteDone = null;
      if (d) setTimeout(d, 200);
    }

    function updateNoteCount() {
      if (noteCount && noteField) noteCount.textContent = noteField.value.length + '/250 characters';
    }

    if (noteField) noteField.addEventListener('input', updateNoteCount);
    var noteSkip = overlay.querySelector('[data-ct-note-skip]');
    var noteSend = overlay.querySelector('[data-ct-note-send]');
    var noteClose = overlay.querySelector('[data-ct-note-close]');
    if (noteSkip) noteSkip.addEventListener('click', function () { finishNote(false); });
    if (noteSend) noteSend.addEventListener('click', function () { finishNote(true); });
    if (noteClose) noteClose.addEventListener('click', function () { finishNote(false); });

    /* ---- compare ---- */
    var listEl = overlay.querySelector('[data-ct-list]');

    function renderList() {
      if (!listEl) return;
      listEl.innerHTML = picks.map(function (p) {
        var v = votes[p.id];

        /* The note you wrote, where you can actually find it again.
           Without this it went into the "say why" sheet and vanished —
           the takes came back on the list but the words didn't, so
           there was no way to check or change what you'd said. */
        var note = notes[p.id];
        var noteHTML = note
          ? '<button type="button" class="ct-row-note" data-ct-editnote="' + p.id + '">' +
              '<span class="ct-row-note-text">“' + esc(note) + '”</span>' +
              '<span class="ct-row-note-edit">Edit</span>' +
            '</button>'
          : '<button type="button" class="ct-row-note ct-row-note--empty" data-ct-editnote="' + p.id + '">Say why</button>';

        return '' +
          '<div class="ct-row" data-pick="' + p.id + '">' +
            (p.thumb ? '<img class="ct-row-thumb" src="' + esc(p.thumb) + '" alt="">' : '<span class="ct-row-thumb"></span>') +
            '<div class="ct-row-info">' +
              '<p class="ct-row-name">' + esc(p.brand) + '</p>' +
              '<p class="ct-row-desc">' + esc(p.name) + '</p>' +
              '<p class="ct-row-price">' + esc(p.price) + '</p>' +
              noteHTML +
            '</div>' +
            '<div class="ct-row-votes">' +
              '<button type="button" class="ct-rv ct-rv--nah' + (v === 'nah' ? ' on' : '') + '" data-v="nah" aria-label="Nah">' +
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg></button>' +
              '<button type="button" class="ct-rv ct-rv--yes' + (v === 'yes' ? ' on' : '') + '" data-v="yes" aria-label="Yes">' +
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 1 0-7.1 7.1L12 21l8.8-8.3a5 5 0 0 0 0-7.1z"/></svg></button>' +
            '</div>' +
          '</div>';
      }).join('');

      listEl.querySelectorAll('.ct-row').forEach(function (row) {
        var id = Number(row.getAttribute('data-pick'));
        row.querySelectorAll('[data-v]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var k = btn.getAttribute('data-v');
            // Un-voting can't be sent to the backend (a take is up or
            // down, there's no "no opinion" row) — so tapping the one
            // that's already on just leaves it as it is.
            if (votes[id] === k) return;
            votes[id] = k;
            queueSave();
            renderList();
          });
        });

        var noteBtn = row.querySelector('[data-ct-editnote]');
        if (noteBtn) {
          noteBtn.addEventListener('click', function () {
            // A note with no take attached would have nothing to save
            // against, so writing one counts as keeping it.
            if (!votes[id]) { votes[id] = 'yes'; queueSave(); }
            openNote(id, renderList);
          });
        }
      });
    }

    function renderRecap() {
      var yes = 0, nah = 0;
      picks.forEach(function (p) {
        if (votes[p.id] === 'yes') yes++;
        else if (votes[p.id] === 'nah') nah++;
      });
      var el = overlay.querySelector('[data-ct-recap]');
      if (el) el.textContent = 'You said yes to ' + yes + ', nah to ' + nah;
    }

    /* ---- sheets ---- */
    function anySheetOpen() {
      return Object.keys(sheets).some(function (k) { return sheets[k].classList.contains('is-open'); });
    }
    function openSheet(name) {
      Object.keys(sheets).forEach(function (k) { sheets[k].classList.toggle('is-open', k === name); });
      if (sheetScrim) sheetScrim.classList.add('is-open');
    }
    function closeSheet() {
      Object.keys(sheets).forEach(function (k) { sheets[k].classList.remove('is-open'); });
      if (sheetScrim) sheetScrim.classList.remove('is-open');
    }
    if (sheetScrim) {
      sheetScrim.addEventListener('click', function () {
        if (sheets.note && sheets.note.classList.contains('is-open')) finishNote(false);
        else closeSheet();
      });
    }

    /* ---- gate → Shopify login popup ---- */
    var gateCta = overlay.querySelector('[data-ct-gate-cta]');
    if (gateCta) {
      gateCta.addEventListener('click', function () {
        /* Hands off to the theme's own login sheet — the same one the
           header opens, where "Sign in with your Shopify account" is the
           door Circle needs. Closing this overlay first so the sheet
           isn't opening underneath it. */
        closePopup();
        setTimeout(function () { Api.login(); }, 280);
      });
    }

    /* ---- vote screen controls ---- */
    overlay.querySelectorAll('[data-ct-vote]').forEach(function (b) {
      b.addEventListener('click', function () { castVote(b.getAttribute('data-ct-vote')); });
    });
    // Delegated, not bound once at setup: the "Review your picks" button
    // on the end-of-deck card (renderDeck, above) is built fresh each time
    // the deck runs out, so a one-time querySelectorAll would never see it.
    overlay.addEventListener('click', function (e) {
      var b = e.target.closest('[data-ct-goto]');
      if (b) screen(b.getAttribute('data-ct-goto'));
    });
    var doneBtn = overlay.querySelector('[data-ct-done]');
    if (doneBtn) {
      doneBtn.addEventListener('click', function () {
        flushSaves();
        screen('done');
      });
    }
    var seeListBtn = overlay.querySelector('[data-ct-seelist]');
    if (seeListBtn) {
      seeListBtn.addEventListener('click', function () {
        /* The list is already on this page, behind the overlay (the
           Circle-vote template renders it from the same ?t= token). So
           this closes the overlay and reloads that panel, rather than
           navigating somewhere to show what's underneath already. */
        flushSaves();
        closePopup();
        if (window.VroCircleSharedList && token) {
          setTimeout(function () { window.VroCircleSharedList.reload(token); }, 280);
          return;
        }
        if (listUrl) { window.location.href = listUrl; return; }
        if (token) {
          /* ?view= is carried over when it's there. Until the theme is
             live the page can't have its template assigned in admin, so
             the alternate template is what's holding the list — dropping
             it here would land on an empty page. */
          var view = new URLSearchParams(window.location.search).get('view');
          window.location.href = '/pages/circle-vote?' +
            (view ? 'view=' + encodeURIComponent(view) + '&' : '') +
            't=' + encodeURIComponent(token);
        }
      });
    }

    /* ---- entry points ----
       Opened either by a link that lands on the page carrying ?t=<token>,
       or by the owner previewing their own share from the Ask modal. */
    function open(shareToken, opts) {
      token = shareToken;
      preview = Boolean(opts && opts.preview);
      overlay.classList.toggle('is-preview', preview);
      openPopup();
      screen('gate');

      // A spinner in the gate deck while load() is still in flight — that
      // deck otherwise renders nothing at all until picks arrive (renderDeck
      // bails out on an empty picks array), which read as a blank popup for
      // however long the request took.
      var gateDeck = overlay.querySelector('[data-ct-deck="gate"]');
      if (gateDeck && !picks.length) {
        gateDeck.innerHTML = '<div class="ct-loading"><span class="ct-spinner" aria-hidden="true"></span></div>';
      }

      return load();
    }

    window.VroCircleTake = { open: open, close: closePopup };

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-circle-take-open]');
      if (!trigger) return;
      e.preventDefault();
      var t = trigger.getAttribute('data-circle-take-token') || token;
      if (!t) {
        if (window.showToast) window.showToast('Share this list first to see what your friends get.');
        return;
      }
      open(t, { preview: trigger.hasAttribute('data-circle-take-preview') });
    });

    // A shared link lands on a page with ?t=... — open straight into it.
    var urlToken = new URLSearchParams(window.location.search).get('t');
    if (urlToken) open(urlToken, {});
  }
})();
